// V25.8 — "which QuickBooks company file am I actually reading?"
//
// Renders the live CompanyInfo identity from QuickBooks plus a cross-check of
// the saved chart-of-accounts mapping against the accounts that file returns.
// A disconnect/reconnect onto a different company file shows up here as a
// near-zero mapping overlap, which is otherwise invisible in the app.

import React from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

const box = (bg, border) => ({
  marginTop: 14, padding: '12px 14px', background: bg,
  border: `1px solid ${border}`, borderRadius: 8, fontSize: 12.5,
});

export default function QboFileAudit({ audit, error, loading, onRun }) {
  const bad = audit && (audit.file_switch_suspected || audit.token_record_count > 1 || audit.realm_id_mismatch);
  const ov = audit?.mapping_overlap;

  return (
    <div data-testid="qbo-file-audit">
      <button
        data-testid="verify-qbo-file-btn"
        onClick={onRun}
        disabled={loading}
        style={{
          marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 13px', borderRadius: 6, border: '1px solid #CBD5E1',
          background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600,
          cursor: loading ? 'default' : 'pointer',
        }}
      >
        <RefreshCw size={13} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined} />
        {loading ? 'Asking QuickBooks…' : 'Verify which company file this is'}
      </button>

      {error && (
        <div data-testid="qbo-file-audit-error" style={box('#FEF2F2', '#FCA5A5')}>
          <strong style={{ color: '#991B1B' }}>Could not verify:</strong>{' '}
          <span style={{ color: '#7F1D1D' }}>{error}</span>
        </div>
      )}

      {audit && (
        <div
          data-testid={bad ? 'qbo-file-audit-warning' : 'qbo-file-audit-ok'}
          style={box(bad ? '#FEF2F2' : '#F0FDF4', bad ? '#FCA5A5' : '#86EFAC')}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
            {bad
              ? <AlertTriangle size={16} color="#B91C1C" style={{ flexShrink: 0, marginTop: 1 }} />
              : <CheckCircle2 size={16} color="#15803D" style={{ flexShrink: 0, marginTop: 1 }} />}
            <div style={{ color: bad ? '#7F1D1D' : '#14532D', lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }} data-testid="qbo-file-audit-verdict">
                {audit.verdict}
              </div>
              <div>
                QuickBooks reports this file as{' '}
                <strong>{audit.company?.legal_name || audit.company?.company_name || '(unnamed)'}</strong>
                {audit.company?.qbo_created_at
                  ? ` · file created ${new Date(audit.company.qbo_created_at).toLocaleDateString()}`
                  : ''}
                {' · realm '}{audit.realm_id_in_use}
              </div>
              {ov && (
                <div data-testid="qbo-file-audit-overlap">
                  Chart-of-accounts match: {ov.matched} of {ov.mapped_account_count} mapped accounts
                  exist in this file{ov.overlap_pct !== null ? ` (${ov.overlap_pct}%)` : ''} ·{' '}
                  {ov.accounts_in_file} accounts in the file
                </div>
              )}
              {audit.token_record_count > 1 && (
                <div>Stale token records found: {audit.token_record_count}</div>
              )}
              {audit.realm_id_mismatch && (
                <div>
                  The client record still points at realm {String(audit.realm_id_on_client_record)} while
                  data is being pulled from {audit.realm_id_in_use}.
                </div>
              )}
              {ov?.missing > 0 && (
                <div style={{ marginTop: 4, fontSize: 11.5, opacity: 0.85 }}>
                  Missing account ids (sample): {ov.missing_sample.join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
